import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createVaraus,
  fetchKattauksetAll,
  fetchPaivat,
  fetchVaraukset,
  verifyAdminPassword,
} from "../supabaseClient";

// The password is checked by Postgres, not here: verifyAdminPassword() calls a
// security-definer function that compares a bcrypt hash in a table the anon key
// cannot read. So the password is not in the bundle and not on the wire in
// either direction beyond the attempt itself.
//
// This still only gates the screen. The data tables carry no RLS, so the anon
// key can read and write them directly regardless of this login — see the note
// in supabase/migrations/20260916000000_admin_password.sql.
const SESSION_KEY = "scherzo.gate.ok";

const EMPTY_ROW = {
  paiva: "",
  kattaus: "",
  ihmiset: "",
  varaaja: "",
  puhelin: "",
  sposti: "",
  info: "",
};

// Column order for the reservations table. Anything the table query returns
// that is not listed here is appended, so a new database column shows up
// without this file needing to change.
const VARAUS_COLUMNS = [
  "id",
  "created_at",
  "paiva",
  "kattaus",
  "ihmiset",
  "varaaja",
  "puhelin",
  "sposti",
  "info",
];

const KATTAUS_COLUMNS = ["id", "paiva", "aika", "kapasiteetti", "taynna", "created_at"];

const LABELS = {
  id: "ID",
  paiva: "Päivä",
  kattaus: "Kattaus",
  aika: "Aika",
  kapasiteetti: "Vapaana",
  ihmiset: "Henkilöä",
  varaaja: "Varaaja",
  puhelin: "Puhelin",
  sposti: "Sähköposti",
  info: "Lisätiedot",
  taynna: "Täynnä",
  created_at: "Luotu",
};

// Union of the preferred order and whatever the rows actually carry.
function columnsFor(rows, preferred) {
  const seen = new Set();
  const out = [];
  for (const key of preferred) {
    seen.add(key);
    out.push(key);
  }
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        out.push(key);
      }
    }
  }
  return out;
}

function cellText(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "kyllä" : "ei";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

// created_at comes back as an ISO string, which is unreadable at a glance.
function formatStamp(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Gate() {
  const [authed, setAuthed] = useState(() => {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      // private mode / storage disabled — just start logged out
      return false;
    }
  });
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [checking, setChecking] = useState(false);

  const [varaukset, setVaraukset] = useState([]);
  const [kattaukset, setKattaukset] = useState([]);
  const [paivat, setPaivat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [newRow, setNewRow] = useState(EMPTY_ROW);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [v, k, p] = await Promise.all([
        fetchVaraukset(),
        fetchKattauksetAll(),
        fetchPaivat(),
      ]);
      setVaraukset(v);
      setKattaukset(k);
      setPaivat(p || []);
    } catch (e) {
      setError(e?.message || "Tietojen haku epäonnistui.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  async function onLogin(e) {
    e.preventDefault();
    if (checking) return;
    setChecking(true);
    setLoginError(null);
    try {
      const ok = await verifyAdminPassword(password);
      if (!ok) {
        setLoginError("Väärä salasana.");
        return;
      }
      try {
        window.sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        // not fatal: the session just will not survive a reload
      }
      setPassword("");
      setAuthed(true);
    } catch (err) {
      setLoginError(err?.message || "Kirjautuminen epäonnistui.");
    } finally {
      setChecking(false);
    }
  }

  function onLogout() {
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch {
      // ignore
    }
    setAuthed(false);
    setVaraukset([]);
    setKattaukset([]);
  }

  // Sittings belong to a day, so the sitting choices follow the chosen day.
  const kattausOptions = useMemo(() => {
    if (!newRow.paiva) return kattaukset;
    return kattaukset.filter((k) => String(k.paiva) === String(newRow.paiva));
  }, [kattaukset, newRow.paiva]);

  function setField(name, value) {
    setNewRow((prev) => {
      const next = { ...prev, [name]: value };
      // a sitting from the previous day would be a mismatched pair
      if (name === "paiva") next.kattaus = "";
      return next;
    });
  }

  async function onAddRow(e) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      // createVaraus also decrements the sitting's kapasiteetti, so a
      // reservation added here consumes seats exactly like a booked one.
      await createVaraus({
        paiva: Number(newRow.paiva),
        kattaus: Number(newRow.kattaus),
        ihmiset: Number(newRow.ihmiset) || null,
        varaaja: newRow.varaaja || null,
        puhelin: newRow.puhelin || null,
        sposti: newRow.sposti || null,
        info: newRow.info || null,
      });
      setNewRow(EMPTY_ROW);
      await load();
    } catch (err) {
      setSaveError(err?.message || "Rivin lisäys epäonnistui.");
    } finally {
      setSaving(false);
    }
  }

  // paiva and kattaus are foreign keys. Shown raw they are just numbers, so
  // resolve them against the rows already loaded for the add-row selects.
  const paivaById = useMemo(
    () => new Map(paivat.map((p) => [String(p.id), p.paiva ?? String(p.id)])),
    [paivat]
  );
  const kattausById = useMemo(
    () => new Map(kattaukset.map((k) => [String(k.id), k.aika ?? String(k.id)])),
    [kattaukset]
  );

  // How one cell renders, given its column name.
  const renderCell = useCallback(
    (col, value) => {
      if (col === "created_at") return formatStamp(value);
      if (col === "paiva") return paivaById.get(String(value)) ?? cellText(value);
      if (col === "kattaus") return kattausById.get(String(value)) ?? cellText(value);
      return cellText(value);
    },
    [paivaById, kattausById]
  );

  // Seating overview: every day, each of its sittings, and the parties booked
  // into it. Largest party first, which is the order the room gets laid out in.
  const overview = useMemo(() => {
    const byKattaus = new Map();
    for (const v of varaukset) {
      const key = String(v.kattaus);
      if (!byKattaus.has(key)) byKattaus.set(key, []);
      byKattaus.get(key).push(v);
    }
    for (const list of byKattaus.values()) {
      list.sort((a, b) => (Number(b.ihmiset) || 0) - (Number(a.ihmiset) || 0));
    }

    return paivat.map((p) => {
      const sittings = kattaukset
        .filter((k) => String(k.paiva) === String(p.id))
        .sort((a, b) => String(a.aika ?? "").localeCompare(String(b.aika ?? "")))
        .map((k) => {
          const rows = byKattaus.get(String(k.id)) ?? [];
          return {
            kattaus: k,
            rows,
            seated: rows.reduce((sum, r) => sum + (Number(r.ihmiset) || 0), 0),
          };
        });
      return { paiva: p, sittings };
    });
  }, [paivat, kattaukset, varaukset]);

  const varausCols = columnsFor(varaukset, VARAUS_COLUMNS);
  const kattausCols = columnsFor(kattaukset, KATTAUS_COLUMNS);

  const styles = (
    <style>{`
      .gate { width: 100%; max-width: 1100px; margin: 0 auto; padding: 24px 16px 64px; text-align: left; }
      .gate h2 { margin: 0 0 4px; }
      .gate .muted { color: #666; font-size: 0.9em; }

      .gate-login { max-width: 360px; margin: 12vh auto 0; text-align: center; }
      .gate-login input { width: 100%; padding: 10px 12px; font-size: 1em; margin: 12px 0; }
      .gate-login button { padding: 10px 22px; font-size: 1em; cursor: pointer; }

      .gate-overview { display: flex; flex-wrap: wrap; gap: 16px; margin: 8px 0 32px; }
      .gate-day { flex: 1 1 280px; min-width: 260px; background: rgba(255,255,255,0.75); padding: 12px 14px; border: 1px solid #ddd; }
      .gate-day h4 { margin: 0 0 8px; }
      .gate-sitting { border: 1px solid #ccc; padding: 8px 10px; margin: 0 0 12px; }
      .gate-sitting:last-child { margin-bottom: 0; }
      .gate-sitting .when { font-weight: 700; display: flex; justify-content: space-between; gap: 8px; border-bottom: 1px solid #e5e5e5; padding-bottom: 4px; }
      .gate-sitting .when span { font-weight: 400; color: #666; font-size: 0.85em; white-space: nowrap; }
      .gate-sitting ul { list-style: none; margin: 4px 0 0; padding: 0; }
      .gate-sitting li { border: 1px solid #ccc; padding: 3px 6px; margin-top: 4px; }
      .gate-sitting li b { display: inline-block; min-width: 2.4em; }
      /* A reservation with a note needs reading before service, so it is the
         one thing on the card that is impossible to skim past. */
      .gate-sitting li.has-info { background: #e8ff7a; border-color: #c2d92e; }
      .gate-sitting .note { margin-left: 2.4em; color: #333; font-size: 0.85em; white-space: pre-wrap; font-style: italic; }
      .gate-sitting .none { color: #888; font-size: 0.9em; padding: 2px 0; }

      .gate-head { display: flex; flex-wrap: wrap; gap: 12px; align-items: baseline; justify-content: space-between; }
      .gate-err { color: #b00020; margin: 8px 0; }

      /* Tables can legitimately be wider than a phone; give each its own
         scroller so the page itself never scrolls sideways. */
      .gate-scroll { overflow-x: auto; margin: 8px 0 32px; background: rgba(255,255,255,0.75); }
      .gate table { border-collapse: collapse; width: 100%; font-size: 0.9em; }
      .gate th, .gate td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; white-space: nowrap; vertical-align: top; }
      .gate th { background: #f3f3f3; position: sticky; top: 0; }
      .gate td.wrap { white-space: pre-wrap; min-width: 180px; max-width: 320px; }

      .gate-form { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; margin-bottom: 8px; }
      .gate-form label { display: flex; flex-direction: column; gap: 4px; font-size: 0.85em; }
      .gate-form input, .gate-form select { padding: 6px 8px; font-size: 0.95em; min-width: 140px; }
      .gate-form button { padding: 8px 18px; font-size: 0.95em; cursor: pointer; }
    `}</style>
  );

  if (!authed) {
    return (
      <main className="gate">
        {styles}
        <form className="gate-login" onSubmit={onLogin}>
          <h2>Ylläpito</h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Salasana"
            autoFocus
            autoComplete="current-password"
            aria-label="Salasana"
            disabled={checking}
          />
          <button type="submit" disabled={checking}>
            {checking ? "Tarkistetaan…" : "Kirjaudu"}
          </button>
          {loginError && <div className="gate-err">{loginError}</div>}
        </form>
      </main>
    );
  }

  return (
    <main className="gate">
      {styles}
      <div className="gate-head">
        <h2>Ylläpito</h2>
        <div>
          <button type="button" onClick={load} disabled={loading} style={{ cursor: "pointer" }}>
            {loading ? "Päivitetään…" : "Päivitä"}
          </button>{" "}
          <button type="button" onClick={onLogout} style={{ cursor: "pointer" }}>
            Kirjaudu ulos
          </button>
        </div>
      </div>

      {error && <div className="gate-err">{error}</div>}

      <h3>Kattaukset päivittäin</h3>
      <div className="gate-overview">
        {overview.length === 0 ? (
          <div className="muted">{loading ? "Ladataan…" : "Ei päiviä."}</div>
        ) : (
          overview.map(({ paiva, sittings }) => (
            <section className="gate-day" key={paiva.id}>
              <h4>{paiva.paiva ?? paiva.id}</h4>
              {sittings.length === 0 ? (
                <div className="none">Ei kattauksia.</div>
              ) : (
                sittings.map(({ kattaus, rows, seated }) => (
                  <div className="gate-sitting" key={kattaus.id}>
                    <div className="when">
                      {kattaus.aika ?? kattaus.id}
                      <span>
                        {seated} hlö · {kattaus.kapasiteetti ?? 0} vapaana
                      </span>
                    </div>
                    {rows.length === 0 ? (
                      <div className="none">Ei varauksia.</div>
                    ) : (
                      <ul>
                        {rows.map((r) => (
                          <li key={r.id} className={r.info ? "has-info" : undefined}>
                            <b>{r.ihmiset ?? "?"}x</b> {r.varaaja || "—"}
                            {r.info ? <div className="note">{r.info}</div> : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </section>
          ))
        )}
      </div>

      <h3>Varaukset ({varaukset.length})</h3>

      <form className="gate-form" onSubmit={onAddRow}>
        <label>
          Päivä
          <select value={newRow.paiva} onChange={(e) => setField("paiva", e.target.value)} required>
            <option value="">Valitse…</option>
            {paivat.map((p) => (
              <option key={p.id} value={p.id}>
                {p.paiva ?? p.id}
              </option>
            ))}
          </select>
        </label>
        <label>
          Kattaus
          <select
            value={newRow.kattaus}
            onChange={(e) => setField("kattaus", e.target.value)}
            required
            disabled={!newRow.paiva}
          >
            <option value="">Valitse…</option>
            {kattausOptions.map((k) => (
              <option key={k.id} value={k.id}>
                {k.aika ?? k.id} ({k.kapasiteetti ?? 0} vapaana)
              </option>
            ))}
          </select>
        </label>
        <label>
          Henkilöä
          <input
            type="number"
            min="1"
            value={newRow.ihmiset}
            onChange={(e) => setField("ihmiset", e.target.value)}
            required
            style={{ minWidth: 80 }}
          />
        </label>
        <label>
          Varaaja
          <input value={newRow.varaaja} onChange={(e) => setField("varaaja", e.target.value)} required />
        </label>
        <label>
          Puhelin
          <input value={newRow.puhelin} onChange={(e) => setField("puhelin", e.target.value)} />
        </label>
        <label>
          Sähköposti
          <input type="email" value={newRow.sposti} onChange={(e) => setField("sposti", e.target.value)} />
        </label>
        <label>
          Lisätiedot
          <input value={newRow.info} onChange={(e) => setField("info", e.target.value)} />
        </label>
        <button type="submit" disabled={saving}>
          {saving ? "Lisätään…" : "Lisää rivi"}
        </button>
      </form>
      {saveError && <div className="gate-err">{saveError}</div>}

      <div className="gate-scroll">
        <table>
          <thead>
            <tr>
              {varausCols.map((c) => (
                <th key={c}>{LABELS[c] ?? c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {varaukset.length === 0 ? (
              <tr>
                <td colSpan={varausCols.length}>{loading ? "Ladataan…" : "Ei varauksia."}</td>
              </tr>
            ) : (
              varaukset.map((row) => (
                <tr key={row.id}>
                  {varausCols.map((c) => (
                    <td key={c} className={c === "info" ? "wrap" : undefined}>
                      {renderCell(c, row[c])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h3>Kattaukset ({kattaukset.length})</h3>
      <div className="gate-scroll">
        <table>
          <thead>
            <tr>
              {kattausCols.map((c) => (
                <th key={c}>{LABELS[c] ?? c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {kattaukset.length === 0 ? (
              <tr>
                <td colSpan={kattausCols.length}>{loading ? "Ladataan…" : "Ei kattauksia."}</td>
              </tr>
            ) : (
              kattaukset.map((row) => (
                <tr key={row.id}>
                  {kattausCols.map((c) => (
                    <td key={c}>{renderCell(c, row[c])}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default Gate;
